/**
 * Hook para gerenciamento de Respostas em Andamentos de Tarefas
 * Suporta @menções e vinculação ao tenant
 * Espelha useActivityComments para o módulo de tarefas
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-07 v1.0.0 - Criação inicial
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { extractMentionIds, formatMentionsForDisplay } from '@/hooks/useActivityComments';
import { uploadTaskAttachmentDirect } from '@/hooks/useTaskAttachments';
import { toast } from 'sonner';

// Logger do módulo
const logger = createLogger('TaskCommentReplies');

/**
 * Interface da resposta em andamento
 */
export interface TaskCommentReply {
  id: string;
  tenant_id: string;
  task_comment_id: string;
  content: string;
  author_id: string;
  created_at: string;
  updated_at: string;
  /** Dados do autor carregados */
  author?: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  /** Menções carregadas */
  mentions?: TaskReplyMention[];
  /** Anexos vinculados à resposta */
  attachments?: TaskReplyAttachment[];
}

/**
 * Interface da menção em resposta
 */
export interface TaskReplyMention {
  id: string;
  reply_id: string;
  mentioned_user_id: string;
  read_at: string | null;
  created_at: string;
}

/**
 * Interface do anexo vinculado a uma resposta
 */
export interface TaskReplyAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number | null;
  public_url: string;
  thumbnail_url: string | null;
}
/**
 * Hook para respostas de um andamento específico
 */
export function useTaskCommentReplies(taskCommentId: string | null, taskId?: string) {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();

  // Ref para rastrear mutations locais e evitar double-refetch
  const localMutationRef = useRef<number | null>(null);

  // Query para buscar respostas
  const repliesQuery = useQuery({
    queryKey: ['task-comment-replies', taskCommentId],
    queryFn: async () => {
      if (!taskCommentId || !currentTenant?.id) return [];

      logger.debug('Buscando respostas do andamento via RPC:', taskCommentId);

      // Busca consolidada via RPC server-side (replies + autores + menções + anexos)
      const { data, error } = await supabase.rpc('get_task_comment_replies', {
        p_task_comment_id: taskCommentId,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar respostas via RPC:', error.message);
        throw error;
      }

      // RPC retorna JSONB — parseia se necessário
      const replies = (typeof data === 'string' ? JSON.parse(data) : data) as TaskCommentReply[];
      return replies || [];
    },
    enabled: !!taskCommentId && !!currentTenant?.id,
    staleTime: 30 * 1000,
  });

  // =========================================================================
  // REALTIME: Escuta mudanças em respostas do andamento
  // =========================================================================
  useEffect(() => {
    if (!taskCommentId || !currentTenant?.id) return;

    logger.debug('Iniciando canal Realtime para respostas:', taskCommentId);

    const channelName = `task-comment-replies:${taskCommentId}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'task_comment_replies',
          filter: `task_comment_id=eq.${taskCommentId}`,
        },
        (payload) => {
          logger.debug('Evento Realtime recebido:', payload.eventType);

          // Se foi mutation local recente (< 2s), ignora
          if (localMutationRef.current) {
            const elapsed = Date.now() - localMutationRef.current;
            if (elapsed < 2000) {
              logger.debug('Ignorando evento Realtime - mutation local recente');
              localMutationRef.current = null;
              return;
            }
          }

          queryClient.invalidateQueries({
            queryKey: ['task-comment-replies', taskCommentId],
            refetchType: 'active',
          });
        }
      )
      .subscribe((status) => {
        logger.debug(`Canal ${channelName} status:`, status);
      });

    return () => {
      logger.debug('Removendo canal Realtime:', channelName);
      supabase.removeChannel(channel);
    };
  }, [taskCommentId, currentTenant?.id, queryClient]);

  // Mutation para adicionar resposta
  const addReply = useMutation({
    onMutate: () => {
      localMutationRef.current = Date.now();
    },
    mutationFn: async (input: { content: string; mentionedUserIds?: string[]; files?: File[] }) => {
      if (!taskCommentId || !currentTenant?.id || !user?.id) {
        throw new Error('Contexto inválido para adicionar resposta');
      }

      logger.debug('Adicionando resposta:', { taskCommentId, content: input.content });

      // 1. Inserir resposta
      const { data: reply, error: replyError } = await supabase
        .from('task_comment_replies')
        .insert({
          tenant_id: currentTenant.id,
          task_comment_id: taskCommentId,
          content: input.content,
          author_id: user.id,
        })
        .select('*')
        .single();

      if (replyError) {
        logger.error('Erro ao criar resposta:', replyError.message);
        throw replyError;
      }

      // 2. Extrair e inserir menções
      const mentionIds = input.mentionedUserIds || extractMentionIds(input.content);

      if (mentionIds.length > 0) {
        const mentions = mentionIds.map(userId => ({
          reply_id: reply.id,
          mentioned_user_id: userId,
        }));

        const { error: mentionError } = await supabase
          .from('task_comment_reply_mentions')
          .insert(mentions);

        if (mentionError) {
          logger.warn('Erro ao inserir menções:', mentionError.message);
        }
      }

      // 3. Upload de arquivos anexos (se houver)
      if (input.files && input.files.length > 0 && taskId) {
        for (const file of input.files) {
          try {
            await uploadTaskAttachmentDirect(
              taskId,
              currentTenant.id,
              file,
              taskCommentId,   // vincula ao andamento
              reply.id         // vincula à resposta
            );
          } catch (uploadErr) {
            logger.warn('Erro ao fazer upload de anexo na resposta:', (uploadErr as Error).message);
          }
        }
      }

      // Busca profile do autor
      const { data: authorProfile } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url')
        .eq('id', user.id)
        .single();

      return {
        ...reply,
        author: authorProfile || null,
      } as TaskCommentReply;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['task-comment-replies', taskCommentId],
        refetchType: 'active',
      });
      toast.success('Comentário adicionado!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao adicionar resposta:', error.message);
      toast.error(`Erro ao adicionar comentário: ${error.message}`);
    },
  });

  // Mutation para deletar resposta
  const deleteReply = useMutation({
    onMutate: () => {
      localMutationRef.current = Date.now();
    },
    mutationFn: async (replyId: string) => {
      logger.debug('Removendo resposta:', replyId);

      const { error } = await supabase
        .from('task_comment_replies')
        .delete()
        .eq('id', replyId)
        .eq('author_id', user?.id);

      if (error) {
        logger.error('Erro ao remover resposta:', error.message);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['task-comment-replies', taskCommentId],
        refetchType: 'active',
      });
      toast.success('Comentário removido!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao remover resposta:', error.message);
      toast.error(`Erro ao remover comentário: ${error.message}`);
    },
  });

  // Mutation para editar resposta (autor ou admin)
  const editReply = useMutation({
    onMutate: () => {
      localMutationRef.current = Date.now();
    },
    mutationFn: async ({ replyId, content }: { replyId: string; content: string }) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');

      logger.debug('Editando resposta:', { replyId, content });

      const { error } = await supabase.rpc('update_task_comment_reply', {
        p_id: replyId,
        p_content: content,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao editar resposta:', error.message);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['task-comment-replies', taskCommentId],
        refetchType: 'active',
      });
      toast.success('Comentário atualizado!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao editar resposta:', error.message);
      toast.error(`Erro ao editar comentário: ${error.message}`);
    },
  });

  // Mutation para marcar menção como lida
  const markMentionAsRead = useMutation({
    mutationFn: async (mentionId: string) => {
      if (!user?.id) {
        throw new Error('Usuário não autenticado');
      }

      logger.debug('Marcando menção como lida:', mentionId);

      const { error } = await supabase
        .from('task_comment_reply_mentions')
        .update({ read_at: new Date().toISOString() })
        .eq('id', mentionId)
        .eq('mentioned_user_id', user.id);

      if (error) {
        logger.error('Erro ao marcar menção como lida:', error.message);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['task-comment-replies', taskCommentId],
        refetchType: 'active',
      });
      toast.success('Menção marcada como lida!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao marcar menção como lida:', error.message);
      toast.error(`Erro ao marcar menção: ${error.message}`);
    },
  });

  return {
    replies: repliesQuery.data || [],
    isLoading: repliesQuery.isLoading,
    error: repliesQuery.error,
    addReply,
    editReply,
    deleteReply,
    markMentionAsRead,
  };
}

// Re-exporta formatMentionsForDisplay para uso nos componentes de tarefa
export { formatMentionsForDisplay };
